import React, { useState, useEffect } from 'react';
import { 
  Edit, Trash2, Shield, ShieldOff, Phone, Mail, Calendar, 
  User as UserIcon, ChevronDown, ChevronUp, Hash, Clock, 
  BadgeCheck, XCircle, Search, Filter, X
} from 'lucide-react';
import Form from '../components/Form';
import apiService from '../services/api';
import { User, FormField } from '../types/api';

interface UserCardProps {
  user: User;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (user: User) => void;
  onBlock: (user: User) => void;
  onVerify: (user: User) => void;
  onDelete: (user: User) => void;
}

const UserCard: React.FC<UserCardProps> = ({ 
  user, isExpanded, onToggle, onEdit, onBlock, onVerify, onDelete 
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getInitials = (user: User) => {
    return `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase();
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      'from-violet-500 to-purple-600',
      'from-blue-500 to-cyan-500',
      'from-emerald-500 to-teal-500',
      'from-orange-500 to-amber-500',
      'from-pink-500 to-rose-500',
      'from-indigo-500 to-blue-600',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="user-card">
      {/* Card Header - Always Visible */}
      <div className="user-card-header" onClick={onToggle}>
        <div className="user-card-left">
          <div className={`user-avatar-large bg-gradient-to-br ${getAvatarColor(user.first_name)}`}>
            {getInitials(user)}
          </div>
          <div className="user-card-info">
            <div className="user-card-name">
              {user.first_name} {user.middle_name} {user.last_name}
            </div>
            <div className="user-card-meta">
              <span className="user-card-id">
                <Hash size={12} />
                {user.sbu_id}
              </span>
              <span className="user-card-email">
                <Mail size={12} />
                {user.email}
              </span>
            </div>
          </div>
        </div>
        
        <div className="user-card-right">
          <div className="user-card-badges">
            <span className={`user-badge ${user.role === 'admin' ? 'badge-admin' : user.role === 'moderator' ? 'badge-mod' : 'badge-user'}`}>
              {user.role}
            </span>
            <span className={`user-badge ${user.blocked ? 'badge-blocked' : 'badge-active'}`}>
              {user.blocked ? 'Blocked' : 'Active'}
            </span>
            <span className={`user-badge ${user.verified ? 'badge-verified' : 'badge-pending'}`}>
              {user.verified ? (
                <><BadgeCheck size={12} /> Verified</>
              ) : (
                <><XCircle size={12} /> Pending</>
              )}
            </span>
          </div>
          <button className="expand-btn" aria-label="Toggle details">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      <div className={`user-card-body ${isExpanded ? 'expanded' : ''}`}>
        <div className="user-details-grid">
          {/* Personal Information */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <UserIcon size={16} />
              Personal Information
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Full Name</span>
                <span className="detail-value">
                  {user.first_name} {user.middle_name} {user.last_name}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Gender</span>
                <span className="detail-value capitalize">{user.gender || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Date of Birth</span>
                <span className="detail-value">
                  <Calendar size={14} className="inline mr-1" />
                  {formatDate(user.date_of_birth)}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <Phone size={16} />
              Contact Information
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Email</span>
                <span className="detail-value">
                  <Mail size={14} className="inline mr-1 text-blue-500" />
                  {user.email}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Mobile Number</span>
                <span className="detail-value">
                  {user.mobile_number ? (
                    <>
                      <Phone size={14} className="inline mr-1 text-green-500" />
                      {user.mobile_number}
                    </>
                  ) : (
                    <span className="text-gray-400">Not provided</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <Hash size={16} />
              Account Information
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">SBU ID</span>
                <span className="detail-value font-mono">{user.sbu_id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">User ID (UID)</span>
                <span className="detail-value font-mono text-xs">{user.uid}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Object ID</span>
                <span className="detail-value font-mono text-xs">{user.object_id}</span>
              </div>
            </div>
          </div>

          {/* Status & Activity */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <Clock size={16} />
              Status & Activity
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Account Status</span>
                <span className={`detail-value ${user.blocked ? 'text-red-600' : 'text-green-600'}`}>
                  {user.blocked ? 'Blocked' : 'Active'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Verification</span>
                <span className={`detail-value ${user.verified ? 'text-green-600' : 'text-amber-600'}`}>
                  {user.verified ? 'Verified' : 'Pending Verification'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Last Login</span>
                <span className="detail-value">
                  {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                </span>
              </div>
              {user.deletedAt && (
                <div className="detail-item">
                  <span className="detail-label">Deleted At</span>
                  <span className="detail-value text-red-600">
                    {formatDate(user.deletedAt)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="user-card-actions">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(user); }}
            className="action-btn action-edit"
            title="Edit User"
          >
            <Edit size={16} />
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onBlock(user); }}
            className={`action-btn ${user.blocked ? 'action-unblock' : 'action-block'}`}
            title={user.blocked ? 'Unblock User' : 'Block User'}
          >
            {user.blocked ? <Shield size={16} /> : <ShieldOff size={16} />}
            {user.blocked ? 'Unblock' : 'Block'}
          </button>
          {!user.verified && (
            <button
              onClick={(e) => { e.stopPropagation(); onVerify(user); }}
              className="action-btn action-verify"
              title="Verify User"
            >
              <BadgeCheck size={16} />
              Verify
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(user); }}
            className="action-btn action-delete"
            title="Delete User"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (userId: string) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedUsers(new Set(filteredUsers.map(u => u.uid)));
  };

  const collapseAll = () => {
    setExpandedUsers(new Set());
  };

  const handleBlockUser = async (user: User) => {
    try {
      await apiService.blockUser(user.uid);
      setSuccess(`User ${user.first_name} ${user.blocked ? 'unblocked' : 'blocked'} successfully`);
      await fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to block user');
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (window.confirm(`Are you sure you want to delete ${user.first_name} ${user.last_name}?`)) {
      try {
        await apiService.deleteUser(user.uid);
        setSuccess(`User ${user.first_name} deleted successfully`);
        await fetchUsers();
      } catch (err: any) {
        setError(err.message || 'Failed to delete user');
      }
    }
  };

  const handleVerifyUser = async (user: User) => {
    try {
      await apiService.verifyUser(user.uid);
      setSuccess(`User ${user.first_name} verified successfully`);
      await fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to verify user');
    }
  };

  const handleFormSubmit = async (formData: Record<string, any>) => {
    try {
      setFormLoading(true);
      setError(null);
      setSuccess(null);
      
      if (editingUser) {
        const updateData = {
          ...editingUser,
          ...formData,
          object_id: editingUser.object_id,
          sbu_id: editingUser.sbu_id,
        };
        
        if (!window.confirm('Are you sure you want to update this user?')) {
          setFormLoading(false);
          return;
        }
        
        await apiService.updateUser(editingUser.uid, updateData as User);
        setShowForm(false);
        setEditingUser(null);
        setSuccess('User updated successfully!');
        await fetchUsers();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingUser(null);
    setError(null);
    setSuccess(null);
  };

  const userFormFields: FormField[] = [
    {
      name: 'first_name',
      label: 'First Name',
      type: 'text',
      required: true,
      validation: { minLength: 2, maxLength: 50 }
    },
    {
      name: 'middle_name',
      label: 'Middle Name',
      type: 'text',
      validation: { maxLength: 50 }
    },
    {
      name: 'last_name',
      label: 'Last Name',
      type: 'text',
      required: true,
      validation: { minLength: 2, maxLength: 50 }
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      validation: { 
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ 
      }
    },
    {
      name: 'date_of_birth',
      label: 'Date of Birth',
      type: 'date',
      required: true
    },
    {
      name: 'gender',
      label: 'Gender',
      type: 'select',
      required: true,
      options: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' }
      ]
    },
    {
      name: 'mobile_number',
      label: 'Mobile Number',
      type: 'text',
      validation: { 
        pattern: /^[+]?[\d\s-]{10,15}$/
      }
    },
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      required: true,
      options: [
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' },
        { value: 'moderator', label: 'Moderator' }
      ]
    }
  ];

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchQuery === '' || 
      `${user.first_name} ${user.middle_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.sbu_id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && !user.blocked) ||
      (filterStatus === 'blocked' && user.blocked) ||
      (filterStatus === 'verified' && user.verified) ||
      (filterStatus === 'pending' && !user.verified);

    return matchesSearch && matchesRole && matchesStatus;
  });

  // Clear all messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="users-page">
      {/* Header */}
      <div className="users-header">
        <div>
          <h2 className="users-title">Users Management</h2>
          <p className="users-subtitle">Manage all registered users • {users.length} total users</p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="message message-error">
          <XCircle size={18} />
          {error}
          <button onClick={() => setError(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="message message-success">
          <BadgeCheck size={18} />
          {success}
          <button onClick={() => setSuccess(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Edit Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <Edit size={20} />
                Edit User: {editingUser?.first_name} {editingUser?.last_name}
              </h3>
              <button onClick={handleCancelEdit} className="modal-close">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <Form
                fields={userFormFields}
                onSubmit={handleFormSubmit}
                loading={formLoading}
                initialData={editingUser || {}}
                submitText="Update User"
              />
            </div>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="users-toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, or SBU ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="search-clear">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filters-group">
          <div className="filter-item">
            <Filter size={16} />
            <select 
              value={filterRole} 
              onChange={(e) => setFilterRole(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="moderator">Moderator</option>
            </select>
          </div>

          <div className="filter-item">
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="expand-controls">
            <button onClick={expandAll} className="expand-btn-small" title="Expand All">
              <ChevronDown size={16} /> Expand All
            </button>
            <button onClick={collapseAll} className="expand-btn-small" title="Collapse All">
              <ChevronUp size={16} /> Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="results-count">
        Showing {filteredUsers.length} of {users.length} users
      </div>

      {/* User Cards */}
      <div className="users-list">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <UserIcon size={48} className="text-gray-300" />
            <h3>No users found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          filteredUsers.map(user => (
            <UserCard
              key={user.uid}
              user={user}
              isExpanded={expandedUsers.has(user.uid)}
              onToggle={() => toggleExpanded(user.uid)}
              onEdit={handleEditUser}
              onBlock={handleBlockUser}
              onVerify={handleVerifyUser}
              onDelete={handleDeleteUser}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default Users;
